import React, { useState, useEffect } from 'react';
import CustomDialog from '../components/CustomDialog';
import AdminNav from './AdminNav';
import './AdminLayout.css';
import './AdminProducts.css';

import { API, authHeaders, authHeadersOnly } from '../services/apiConfig';

const BASE = API;

const resolveImg = (url) => {
  if (!url) return 'images/product.jpg';
  if (url.startsWith('http')) return url;
  // Add cache-busting timestamp to force browser to reload
  return `${BASE}${url}?t=${Date.now()}`;
};

const deleteImageFromServer = async (imageUrl) => {
  if (!imageUrl || imageUrl.startsWith('blob:') || !imageUrl.includes('/uploads/')) return;
  try {
    await fetch(`${BASE}/api/products/image?imageUrl=${encodeURIComponent(imageUrl)}`, { method: 'DELETE', headers: authHeadersOnly() });
  } catch (e) { console.warn('Image delete failed:', e); }
};

const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${BASE}/api/products/upload-image`, { method: 'POST', body: fd, headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` } });
  if (!res.ok) throw new Error('Image upload failed');
  return await res.text(); // returns /uploads/products/filename
};

const emptyVariant = () => ({ color: '', size: '', stock: '', price: '', isDefault: false, imageFiles: [], imagePreviews: [], imageUrls: [] });

function ManageProducts() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [dialog, setDialog] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', details: '', price: '', quantity: '',
    category: 'STANDARD', type: 'CORPORATE_ADD_ONS', mainImage: '', specifications: {}
  });
  const [specRows, setSpecRows] = useState([{ key: '', value: '' }]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [extraImageFiles, setExtraImageFiles] = useState([]);
  const [extraImagePreviews, setExtraImagePreviews] = useState([]);
  const [existingExtraImages, setExistingExtraImages] = useState([]);
  const [variants, setVariants] = useState([]);

  useEffect(() => { fetchProducts(); }, []);
  useEffect(() => { filterProducts(); }, [searchTerm, categoryFilter, products]);

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${BASE}/api/products`, { headers: authHeadersOnly() });
      const data = await res.json();
      setProducts(data);
    } catch (e) { console.error(e); }
  };

  const filterProducts = () => {
    let f = products;
    if (searchTerm) f = f.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.description.toLowerCase().includes(searchTerm.toLowerCase()));
    if (categoryFilter !== 'ALL') f = f.filter(p => p.category === categoryFilter);
    setFilteredProducts(f);
  };

  const openAddModal = () => {
    setEditingProduct(null);
    setFormData({ name: '', description: '', details: '', price: '', quantity: '', category: 'STANDARD', type: 'CORPORATE_ADD_ONS', mainImage: '', specifications: {} });
    setSpecRows([{ key: '', value: '' }]);
    setImageFile(null);
    setImagePreview('');
    setExtraImageFiles([]);
    setExtraImagePreviews([]);
    setExistingExtraImages([]);
    setVariants([]);
    setShowModal(true);
  };

  const openEditModal = async (product) => {
    setEditingProduct(product);
    setFormData({
      productId: product.productId || '',
      name: product.name, description: product.description, details: product.details || '',
      price: product.price, quantity: product.quantity, category: product.category,
      type: product.type || 'CORPORATE_ADD_ONS', mainImage: product.mainImage || '',
      specifications: product.specifications || {}
    });
    const specs = product.specifications || {};
    const rows = Object.entries(specs).map(([key, value]) => ({ key, value: String(value) }));
    setSpecRows(rows.length ? rows : [{ key: '', value: '' }]);
    setImageFile(null);
    setImagePreview(product.mainImage ? resolveImg(product.mainImage) : '');
    setExtraImageFiles([]);
    setExtraImagePreviews((product.additionalImages || []).map(resolveImg));
    setExistingExtraImages(product.additionalImages || []);

    // load existing variants
    try {
      const res = await fetch(`${BASE}/api/products/${product.id}/variants`, { headers: authHeadersOnly() });
      const data = await res.json();
      setVariants(data.map(v => ({
        id: v.id,
        color: v.color || '', size: v.size || '',
        stock: v.stock || '', price: v.price || '',
        isDefault: v.isDefault || false,
        imageFiles: [], imagePreviews: (v.images || []).map(resolveImg), imageUrls: v.images || []
      })));
    } catch { setVariants([]); }
    setShowModal(true);
  };

  const handleMainImageFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setFormData(prev => ({ ...prev, mainImage: '' }));
  };

  const deleteMainImage = async () => {
    const url = formData.mainImage;
    await deleteImageFromServer(url);
    setFormData(prev => ({ ...prev, mainImage: '' }));
    setImageFile(null);
    setImagePreview('');
  };

  const handleExtraImages = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setExtraImageFiles(prev => [...prev, ...files]);
    setExtraImagePreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
  };

  const removeExtraImage = async (idx) => {
    const existingCount = existingExtraImages.length;
    if (idx < existingCount) {
      await deleteImageFromServer(existingExtraImages[idx]);
      setExistingExtraImages(prev => prev.filter((_, i) => i !== idx));
    } else {
      const fileIdx = idx - existingCount;
      setExtraImageFiles(prev => prev.filter((_, i) => i !== fileIdx));
    }
    setExtraImagePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  // Variant helpers
  const addVariant = () => setVariants(prev => [...prev, emptyVariant()]);
  const removeVariant = (i) => setVariants(prev => prev.filter((_, idx) => idx !== i));
  const updateVariant = (i, field, val) => setVariants(prev => prev.map((v, idx) => idx === i ? { ...v, [field]: val } : v));

  const handleVariantImages = (i, e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const previews = files.map(f => URL.createObjectURL(f));
    setVariants(prev => prev.map((v, idx) => idx === i
      ? { ...v, imageFiles: [...v.imageFiles, ...files], imagePreviews: [...v.imagePreviews, ...previews] }
      : v
    ));
  };

  const removeVariantImage = (vi, ii) => {
    setVariants(prev => prev.map((v, idx) => {
      if (idx !== vi) return v;
      const isExisting = ii < v.imageUrls.length;
      return {
        ...v,
        imageUrls: isExisting ? v.imageUrls.filter((_, i) => i !== ii) : v.imageUrls,
        imageFiles: !isExisting ? v.imageFiles.filter((_, i) => i !== (ii - v.imageUrls.length)) : v.imageFiles,
        imagePreviews: v.imagePreviews.filter((_, i) => i !== ii)
      };
    }));
  };

  const addSpecRow = () => setSpecRows(prev => [...prev, { key: '', value: '' }]);
  const removeSpecRow = (i) => setSpecRows(prev => prev.filter((_, idx) => idx !== i));
  const updateSpecRow = (i, field, val) => setSpecRows(prev => prev.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const specs = {};
      specRows.forEach(({ key, value }) => { if (key.trim()) specs[key.trim()] = value; });

      // Upload main image if file selected
      let imageUrl = formData.mainImage;
      if (imageFile) imageUrl = await uploadImage(imageFile);

      // Upload extra images
      const uploadedExtras = await Promise.all(extraImageFiles.map(f => uploadImage(f)));
      const allExtraImages = [...existingExtraImages, ...uploadedExtras];

      // Upload variant images
      const processedVariants = await Promise.all(variants.map(async (v) => {
        const uploadedUrls = await Promise.all(v.imageFiles.map(f => uploadImage(f)));
        return {
          color: v.color, size: v.size,
          stock: parseInt(v.stock) || 0,
          price: v.price ? parseFloat(v.price) : null,
          isDefault: v.isDefault,
          images: [...v.imageUrls, ...uploadedUrls]
        };
      }));

      const url = editingProduct ? `${BASE}/api/products/${editingProduct.id}` : `${BASE}/api/products`;
      const method = editingProduct ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify({
          productId: formData.productId,
          name: formData.name, description: formData.description, details: formData.details,
          price: parseFloat(formData.price), quantity: parseInt(formData.quantity),
          category: formData.category, type: formData.type,
          mainImage: imageUrl, specifications: specs,
          additionalImages: allExtraImages,
          variants: processedVariants
        })
      });

      if (!res.ok) throw new Error(await res.text());
      const saved = await res.json();

      setProducts(prev => editingProduct
        ? prev.map(p => p.id === saved.id ? saved : p)
        : [...prev, saved]
      );
      setDialog({ message: `Product ${editingProduct ? 'updated' : 'added'} successfully`, type: 'success', onClose: () => setDialog(null) });
      setShowModal(false);
    } catch (err) {
      setDialog({ message: `Failed to save product: ${err.message}`, type: 'error', onClose: () => setDialog(null) });
    }
  };

  const deleteProduct = (id) => {
    setDialog({
      message: 'Are you sure you want to delete this product?', type: 'confirm',
      onClose: () => setDialog(null),
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE}/api/products/${id}`, { method: 'DELETE', headers: authHeadersOnly() });
          if (res.ok) { setDialog({ message: 'Product deleted successfully', type: 'success', onClose: () => setDialog(null) }); fetchProducts(); }
          else setDialog({ message: `Failed to delete: ${await res.text()}`, type: 'error', onClose: () => setDialog(null) });
        } catch (err) { setDialog({ message: err.message, type: 'error', onClose: () => setDialog(null) }); }
      }
    });
  };

  const handleLogout = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href = '/login'; };

  const inputStyle = { width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };
  const smallBtn = (bg, color) => ({ background: bg, color, border: 'none', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' });

  return (
    <>
      <AdminNav onLogout={handleLogout} />
      <div className="admin-page-wrapper">
        <div className="admin-page-container">
          <div className="admin-page-header">
            <div className="admin-products-header">
              <div>
                <h1 className="admin-page-title">Manage Products</h1>
                <p className="admin-page-subtitle">Add, edit and manage product inventory</p>
              </div>
              <button onClick={openAddModal} className="admin-add-product-btn">+ Add Product</button>
            </div>
          </div>

          <div className="admin-search-filter-card">
            <input type="text" placeholder="Search products..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="admin-search-input" />
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="admin-filter-select">
              <option value="ALL">All Categories</option>
              <option value="STANDARD">Standard</option>
              <option value="PREMIUM">Premium</option>
              <option value="SUPER_PREMIUM">Super Premium</option>
            </select>
          </div>

          <div className="admin-products-grid">
            {filteredProducts.map((product) => (
              <div key={product.id} className="admin-product-card">
                <div className="admin-product-image">
                  <img src={resolveImg(product.mainImage)} alt={product.name} />
                </div>
                <div className="admin-product-content">
                  <h3 className="admin-product-name">{product.name}</h3>
                  {product.productId && <p style={{fontSize:'11px',color:'#888',margin:'0 0 4px',fontWeight:'600'}}>ID: {product.productId}</p>}
                  <p className="admin-product-description">{product.description}</p>
                  <div className="admin-product-price">Rs. {product.price}</div>
                  <p className="admin-product-meta">Stock: {product.quantity} | {product.category}</p>
                  <div className="admin-product-actions">
                    <button className="admin-product-btn edit" onClick={() => openEditModal(product)}>Edit</button>
                    <button className="admin-product-btn delete" onClick={() => deleteProduct(product.id)}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {filteredProducts.length === 0 && <div className="admin-empty-state"><i className="fas fa-box"></i><p>No products found</p></div>}
        </div>
      </div>

      {showModal && (
        <div className="admin-product-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-product-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-product-modal-header">
              <h2 className="admin-product-modal-title">{editingProduct ? 'Edit Product' : 'Add Product'}</h2>
            </div>
            <div className="admin-product-modal-body">
              <form onSubmit={handleSubmit}>

                {/* Basic fields */}
                <div className="admin-form-group">
                  <label className="admin-form-label">Product ID <span style={{color:'#ff0000'}}>*</span></label>
                  <input type="text" className="admin-form-input" placeholder="e.g. CW-1001" value={formData.productId} onChange={(e) => setFormData({ ...formData, productId: e.target.value })} required />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Name</label>
                  <input type="text" className="admin-form-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Description</label>
                  <textarea className="admin-form-textarea" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Details</label>
                  <textarea className="admin-form-textarea" value={formData.details} onChange={(e) => setFormData({ ...formData, details: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Price</label>
                  <input type="text" inputMode="decimal" className="admin-form-input" value={formData.price} onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) setFormData({ ...formData, price: e.target.value }); }} required />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Quantity</label>
                  <input type="text" inputMode="numeric" className="admin-form-input" value={formData.quantity} onChange={(e) => { if (/^\d*$/.test(e.target.value)) setFormData({ ...formData, quantity: e.target.value }); }} required />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Category</label>
                  <select className="admin-form-select" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} required>
                    <option value="STANDARD">Standard</option>
                    <option value="PREMIUM">Premium</option>
                    <option value="SUPER_PREMIUM">Super Premium</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Type</label>
                  <select className="admin-form-select" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} required>
                    <option value="ECO_FRIENDLY">Eco Friendly</option>
                    <option value="STATIONERY">Stationery</option>
                    <option value="CORPORATE_ADD_ONS">Corporate Add-Ons</option>
                    <option value="BACKPACKS_AND_SLINGS">Backpacks & Slings</option>
                    <option value="TRAVELLING_ADD_ONS">Travelling Add-Ons</option>
                    <option value="ACCESSORIES">Accessories</option>
                  </select>
                </div>

                {/* Main Image */}
                <div className="admin-form-group">
                  <label className="admin-form-label">Main Product Image</label>
                  <input type="text" className="admin-form-input" placeholder="Paste image URL..." value={formData.mainImage}
                    onChange={(e) => { setFormData({ ...formData, mainImage: e.target.value }); setImageFile(null); setImagePreview(e.target.value); }}
                    style={{ marginBottom: '8px' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <label style={{ cursor: 'pointer', background: '#667eea', color: 'white', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                      📁 Import from Device
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleMainImageFile} />
                    </label>
                    {imagePreview && (
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img src={imagePreview} alt="preview" style={{ height: '64px', width: '64px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #ddd' }} />
                        <button type="button" onClick={deleteMainImage}
                          title="Delete main image"
                          style={{ position: 'absolute', top: '-8px', right: '-8px', width: '22px', height: '22px', borderRadius: '50%', background: '#ff5252', color: 'white', border: '2px solid white', cursor: 'pointer', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>✕</button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Images */}
                <div className="admin-form-group">
                  <label className="admin-form-label">Additional Images</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                    {extraImagePreviews.map((src, idx) => (
                      <div key={idx} style={{ position: 'relative' }}>
                        <img src={src} alt="" style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #ddd' }} />
                        <button type="button" onClick={() => removeExtraImage(idx)}
                          style={{ position: 'absolute', top: '-6px', right: '-6px', width: '18px', height: '18px', borderRadius: '50%', background: '#ff5252', color: 'white', border: 'none', cursor: 'pointer', fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>✕</button>
                      </div>
                    ))}
                    <label style={{ cursor: 'pointer', width: '64px', height: '64px', border: '2px dashed #667eea', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#667eea', fontSize: '24px', fontWeight: '300' }}>
                      +
                      <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleExtraImages} />
                    </label>
                  </div>
                  <p style={{ fontSize: '12px', color: '#999', margin: 0 }}>These appear as a thumbnail slider beside the main image on the product page.</p>
                </div>

                {/* Specifications */}
                <div className="admin-form-group">
                  <label className="admin-form-label">Specifications</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {specRows.map((row, i) => (
                      <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input type="text" className="admin-form-input" placeholder="Key (e.g. Material)" value={row.key} onChange={(e) => updateSpecRow(i, 'key', e.target.value)} style={{ flex: 1 }} />
                        <input type="text" className="admin-form-input" placeholder="Value (e.g. Leather)" value={row.value} onChange={(e) => updateSpecRow(i, 'value', e.target.value)} style={{ flex: 1 }} />
                        <button type="button" onClick={() => removeSpecRow(i)} style={smallBtn('#ff5252', 'white')}>✕</button>
                      </div>
                    ))}
                    <button type="button" onClick={addSpecRow} style={{ alignSelf: 'flex-start', background: '#e8f5e9', color: '#388e3c', border: '1px solid #a5d6a7', borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>+ Add Row</button>
                  </div>
                </div>

                {/* Variants */}
                <div className="admin-form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <label className="admin-form-label" style={{ margin: 0 }}>Product Variants</label>
                    <button type="button" onClick={addVariant} style={{ background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', padding: '7px 16px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>+ Add Variant</button>
                  </div>

                  {variants.length === 0 && (
                    <p style={{ color: '#999', fontSize: '13px', fontStyle: 'italic' }}>No variants added. Click "+ Add Variant" to add color/size options.</p>
                  )}

                  {variants.map((v, i) => (
                    <div key={i} style={{ border: '1.5px solid #e0e0e0', borderRadius: '12px', padding: '16px', marginBottom: '12px', background: '#fafafa', position: 'relative' }}>
                      <button type="button" onClick={() => removeVariant(i)} style={{ position: 'absolute', top: '10px', right: '10px', ...smallBtn('#ff5252', 'white') }}>✕ Remove</button>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                        <div>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Color</label>
                          <input type="text" style={inputStyle} placeholder="e.g. Red" value={v.color} onChange={(e) => updateVariant(i, 'color', e.target.value)} />
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Size</label>
                          <input type="text" style={inputStyle} placeholder="e.g. M, L, XL" value={v.size} onChange={(e) => updateVariant(i, 'size', e.target.value)} />
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Stock</label>
                          <input type="text" inputMode="numeric" style={inputStyle} placeholder="0" value={v.stock} onChange={(e) => { if (/^\d*$/.test(e.target.value)) updateVariant(i, 'stock', e.target.value); }} />
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Variant Price (₹)</label>
                          <input type="text" inputMode="decimal" style={inputStyle} placeholder="Leave blank to use product price" value={v.price} onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) updateVariant(i, 'price', e.target.value); }} />
                        </div>
                      </div>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '10px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={v.isDefault} onChange={(e) => updateVariant(i, 'isDefault', e.target.checked)} />
                        Set as Default Variant
                      </label>

                      {/* Variant Images */}
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>Variant Images</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                          {v.imagePreviews.map((src, ii) => (
                            <div key={ii} style={{ position: 'relative' }}>
                              <img src={src} alt="" style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #ddd' }} />
                              <button type="button" onClick={() => removeVariantImage(i, ii)}
                                style={{ position: 'absolute', top: '-6px', right: '-6px', width: '18px', height: '18px', borderRadius: '50%', background: '#ff5252', color: 'white', border: 'none', cursor: 'pointer', fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>✕</button>
                            </div>
                          ))}
                          <label style={{ cursor: 'pointer', width: '64px', height: '64px', border: '2px dashed #667eea', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#667eea', fontSize: '24px', fontWeight: '300' }}>
                            +
                            <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => handleVariantImages(i, e)} />
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button type="submit" className="admin-form-submit-btn">{editingProduct ? 'Update' : 'Add'} Product</button>
              </form>
            </div>
          </div>
        </div>
      )}

      {dialog && <CustomDialog message={dialog.message} type={dialog.type} onClose={dialog.onClose} onConfirm={dialog.onConfirm} />}
    </>
  );
}

export default ManageProducts;
